import { useState, useEffect, useCallback, useMemo } from "react";

import {
  Box,
  Grid,
  Typography,
  Switch,
  IconButton,
  Chip,
  CircularProgress,
  Autocomplete,
  TextField,
  Tooltip,
  useTheme
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";

import { calculateMergedProductFootprint } from "@aclymatepackages/products";

import GrayPaper from "../../../../atoms/GrayPaper";
import ProductPdfCapture from "./ProductPdfCapture";
import SharedReportOverlay from "../../../../modules/reporting/SharedReportOverlay";

import {
  useAccountData,
  useCachedDisplayData,
  useCachedFirebaseCrud,
  firebaseArrayUnionObj,
  firebaseArrayRemoveObj
} from "../../../../../helpers/firebase";
import { fetchOurApi } from "../../../../../helpers/utils/apiCalls";
import useProductsEmissionsFactors from "../../../../../helpers/contexts/ProductsEmissionsFactors";

const productReportSessionKey = (vendorId, productId) =>
  `product-report-url-${vendorId}-${productId}`;

const ProductStatusAvatar = ({ isFinalized }) => {
  const { palette } = useTheme();
  const bg = isFinalized ? palette.secondary.main : palette.warning.main;
  const label = isFinalized ? "Finalized" : "Preliminary";
  const Icon = isFinalized ? CheckCircleIcon : WarningIcon;

  return (
    <Tooltip title={label}>
      <Box
        style={{
          backgroundColor: bg,
          width: 28,
          height: 28,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Icon style={{ fontSize: 16, color: "white" }} />
      </Box>
    </Tooltip>
  );
};

const reviewStatusChip = (status) => {
  if (status === "reviewed") {
    return <Chip label="Reviewed" color="success" size="small" />;
  }
  if (status === "shared") {
    return <Chip label="Pending Review" color="warning" size="small" />;
  }
  return <Chip label="Not Shared" variant="outlined" size="small" />;
};

const ShareTypeRow = ({ label, isShared, status, onToggle, isLoading }) => (
  <Grid container alignItems="center" spacing={1}>
    <Grid item xs>
      <Typography variant="body2">{label}</Typography>
    </Grid>
    <Grid item>{reviewStatusChip(isShared ? status : null)}</Grid>
    <Grid item>
      {isLoading ? (
        <Box px={1.5}>
          <CircularProgress size={16} />
        </Box>
      ) : (
        <Switch checked={isShared} onChange={onToggle} color="primary" />
      )}
    </Grid>
  </Grid>
);

const SharedProductRow = ({
  product,
  sharedEntry,
  isFinalized,
  isReadOnly,
  onView,
  onUnshare,
  onToggleBom,
  onToggleReport,
  generatingReport
}) => {
  const { bomShared, reportShared, bomStatus, reportStatus, reportUrl } =
    sharedEntry;

  return (
    <Grid container direction="column" spacing={1}>
      <Grid item container alignItems="center" spacing={1} wrap="nowrap">
        <Grid item>
          <ProductStatusAvatar isFinalized={isFinalized} />
        </Grid>
        <Grid item xs>
          <Typography variant="body2">{product.name}</Typography>
          {product.sku && (
            <Typography variant="caption" color="textSecondary">
              {product.sku}
            </Typography>
          )}
        </Grid>
        {reportUrl && reportShared && (
          <Grid item>
            <Tooltip title="View report">
              <IconButton size="small" onClick={onView}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Grid>
        )}
        {!isReadOnly && (
          <Grid item>
            <Tooltip title="Remove product">
              <IconButton size="small" color="error" onClick={onUnshare}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Grid>
        )}
      </Grid>
      {!isReadOnly && (
        <Grid item>
          <Grid container direction="column" spacing={0.5}>
            <Grid item>
              <ShareTypeRow
                label="BOM Data"
                isShared={bomShared}
                status={bomStatus}
                onToggle={onToggleBom}
              />
            </Grid>
            <Grid item>
              <ShareTypeRow
                label="Report"
                isShared={reportShared}
                status={reportStatus}
                onToggle={onToggleReport}
                isLoading={generatingReport}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
      {isReadOnly && (
        <Grid item>
          <Grid container direction="column" spacing={0.5}>
            {bomShared && (
              <Grid item>
                <Grid container alignItems="center" spacing={1}>
                  <Grid item xs>
                    <Typography variant="body2" color="textSecondary">
                      BOM Data
                    </Typography>
                  </Grid>
                  <Grid item>{reviewStatusChip(bomStatus)}</Grid>
                </Grid>
              </Grid>
            )}
            {reportShared && (
              <Grid item>
                <Grid container alignItems="center" spacing={1}>
                  <Grid item xs>
                    <Typography variant="body2" color="textSecondary">
                      Report
                    </Typography>
                  </Grid>
                  <Grid item>{reviewStatusChip(reportStatus)}</Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

const BuyerAssignedProductRow = ({ name, bomStatus }) => {
  const statusLabel =
    {
      requested: "BOM Requested",
      confirmed: "BOM Confirmed",
      pending: "Pending"
    }[bomStatus] || bomStatus;

  return (
    <Grid container alignItems="center" spacing={1} wrap="nowrap">
      <Grid item xs>
        <Typography variant="body2">{name}</Typography>
      </Grid>
      <Grid item>
        <Chip label={statusLabel} size="small" variant="outlined" />
      </Grid>
    </Grid>
  );
};

const ProductsSection = ({
  customer,
  isReadOnly = false,
  onSharedProductsChange,
  buyerAssignedProducts = []
}) => {
  const [{ id: vendorAccountId }] = useAccountData();
  const [dbProducts] = useCachedDisplayData("product-components");
  const [productLifecycleEvents] = useCachedDisplayData(
    "product-lifecycle-events"
  );
  const [{ emissionsFactors }] = useProductsEmissionsFactors();
  const { updateCollectionDoc } = useCachedFirebaseCrud();

  const [generatingProductId, setGeneratingProductId] = useState(null);
  const [viewingProduct, setViewingProduct] = useState(null);
  const [selectorKey, setSelectorKey] = useState(0);
  const [optimisticSharedProducts, setOptimisticSharedProducts] =
    useState(null);
  const [trackedCustomerId, setTrackedCustomerId] = useState(customer.id);

  if (customer.id !== trackedCustomerId) {
    setTrackedCustomerId(customer.id);
    setOptimisticSharedProducts(null);
  }

  const { id: relationshipId, buyerId } = customer;
  const sharedProducts = useMemo(
    () => optimisticSharedProducts ?? (customer.sharedProducts || []),
    [optimisticSharedProducts, customer.sharedProducts]
  );

  const getLifecycleEvent = useCallback(
    (productId) =>
      productLifecycleEvents.find((e) => e.productId === productId) || {},
    [productLifecycleEvents]
  );

  const isProductFinalized = useCallback(
    (productId) => {
      const { bomStatus } = getLifecycleEvent(productId);
      return bomStatus === "confirmed";
    },
    [getLifecycleEvent]
  );

  const getMergedProduct = (productDoc) => {
    const lifecycleEvent = getLifecycleEvent(productDoc.id);
    return calculateMergedProductFootprint(
      productDoc,
      lifecycleEvent,
      emissionsFactors
    );
  };

  const getSharedEntry = (productId) =>
    sharedProducts.find((p) => p.productId === productId) || null;

  useEffect(() => {
    if (isReadOnly) {
      return;
    }
    const hasStale = sharedProducts.some(
      (p) => isProductFinalized(p.productId) && !p.isFinalized
    );
    if (!hasStale) {
      return;
    }
    const updatedProducts = sharedProducts.map((p) =>
      isProductFinalized(p.productId) && !p.isFinalized
        ? { ...p, isFinalized: true, reportUrl: null }
        : p
    );
    setOptimisticSharedProducts(updatedProducts);
    updateCollectionDoc("customers", relationshipId, {
      sharedProducts: updatedProducts
    });
    onSharedProductsChange && onSharedProductsChange(updatedProducts);
  }, [
    productLifecycleEvents,
    sharedProducts,
    isReadOnly,
    relationshipId,
    isProductFinalized,
    onSharedProductsChange,
    updateCollectionDoc
  ]);

  useEffect(() => {
    if (isReadOnly || generatingProductId) {
      return;
    }
    const needsRegen = sharedProducts.find(
      (p) => p.reportShared && !p.reportUrl
    );
    if (!needsRegen) {
      return;
    }
    const canonicalUrl =
      isProductFinalized(needsRegen.productId) && vendorAccountId
        ? sessionStorage.getItem(
            productReportSessionKey(vendorAccountId, needsRegen.productId)
          )
        : null;
    if (!canonicalUrl) {
      setGeneratingProductId(needsRegen.productId);
      return;
    }
    const updatedProducts = sharedProducts.map((p) =>
      p.productId === needsRegen.productId
        ? { ...p, reportUrl: canonicalUrl }
        : p
    );
    setOptimisticSharedProducts(updatedProducts);
    updateCollectionDoc("customers", relationshipId, {
      sharedProducts: updatedProducts
    });
    onSharedProductsChange && onSharedProductsChange(updatedProducts);
  }, [
    sharedProducts,
    isReadOnly,
    generatingProductId,
    isProductFinalized,
    vendorAccountId,
    relationshipId,
    onSharedProductsChange,
    updateCollectionDoc
  ]);

  const updateSharedProducts = async (updatedProducts) => {
    setOptimisticSharedProducts(updatedProducts);
    await updateCollectionDoc("customers", relationshipId, {
      sharedProducts: updatedProducts
    });
    onSharedProductsChange && onSharedProductsChange(updatedProducts);
  };

  const onShare = async ({ id: productId, name: productName }) => {
    const now = new Date();
    const productIsFinalized = isProductFinalized(productId);
    const updatedProducts = [
      ...sharedProducts,
      {
        productId,
        productName,
        sharedAt: now,
        bomShared: false,
        reportShared: false,
        bomStatus: "shared",
        reportStatus: "shared",
        isFinalized: productIsFinalized
      }
    ];
    await updateSharedProducts(updatedProducts);
    await fetchOurApi({
      path: "/products/share-product-with-buyer",
      method: "POST",
      data: {
        productId,
        vendorId: vendorAccountId,
        buyerId,
        bomStatus: productIsFinalized ? "confirmed" : "shared"
      }
    });
  };

  const onUnshare = async (productId) => {
    const updatedProducts = sharedProducts.filter(
      (p) => p.productId !== productId
    );
    await updateSharedProducts(updatedProducts);
    await updateCollectionDoc("product-components", productId, {
      buyerIds: firebaseArrayRemoveObj(buyerId)
    });
  };

  const onToggleBom = async (productId, enabled) => {
    const entry = getSharedEntry(productId);
    const updatedProducts = sharedProducts.map((p) =>
      p.productId === productId ? { ...p, bomShared: enabled } : p
    );
    await updateSharedProducts(updatedProducts);
    if (enabled) {
      const { packaging, route, sku, packagingWeight } =
        getLifecycleEvent(productId);
      await updateCollectionDoc("product-components", productId, {
        buyerIds: firebaseArrayUnionObj(buyerId),
        ...(packaging !== undefined ? { packaging } : {}),
        ...(route !== undefined ? { route } : {}),
        ...(sku !== undefined ? { sku } : {}),
        ...(packagingWeight !== undefined ? { packagingWeight } : {})
      });
    } else if (!entry?.reportShared) {
      await updateCollectionDoc("product-components", productId, {
        buyerIds: firebaseArrayRemoveObj(buyerId)
      });
    }
  };

  const onToggleReport = async (productId, enabled) => {
    const entry = getSharedEntry(productId);
    if (!enabled) {
      const updatedProducts = sharedProducts.map((p) =>
        p.productId === productId ? { ...p, reportShared: false } : p
      );
      await updateSharedProducts(updatedProducts);
      if (!entry?.bomShared) {
        await updateCollectionDoc("product-components", productId, {
          buyerIds: firebaseArrayRemoveObj(buyerId)
        });
      }
      return;
    }
    const newIsFinalized = isProductFinalized(productId);
    if (entry?.reportUrl && newIsFinalized !== entry?.isFinalized) {
      const clearedProducts = sharedProducts.map((p) =>
        p.productId === productId ? { ...p, reportUrl: null } : p
      );
      await updateSharedProducts(clearedProducts);
      setGeneratingProductId(productId);
      return;
    }
    if (entry?.reportUrl) {
      const productDoc = dbProducts.find((p) => p.id === productId);
      const mergedProduct = productDoc ? getMergedProduct(productDoc) : null;
      const updatedProducts = sharedProducts.map((p) =>
        p.productId === productId
          ? {
              ...p,
              reportShared: true,
              ...(mergedProduct?.kgsCo2e !== undefined
                ? { kgsCo2e: mergedProduct.kgsCo2e }
                : {}),
              ...(mergedProduct?.footprintBreakdownKgs !== undefined
                ? { footprintBreakdownKgs: mergedProduct.footprintBreakdownKgs }
                : {})
            }
          : p
      );
      await updateSharedProducts(updatedProducts);
      await updateCollectionDoc("product-components", productId, {
        buyerIds: firebaseArrayUnionObj(buyerId)
      });
      return;
    }
    setGeneratingProductId(productId);
  };

  const onUploadComplete = async (
    productId,
    url,
    kgsCo2e,
    footprintBreakdownKgs
  ) => {
    const newIsFinalized = isProductFinalized(productId);

    if (newIsFinalized && vendorAccountId) {
      sessionStorage.setItem(
        productReportSessionKey(vendorAccountId, productId),
        url
      );
    }

    const updatedProducts = sharedProducts.map((p) => {
      if (p.productId !== productId) {
        return p;
      }
      const bomStatus =
        newIsFinalized && !p.isFinalized && p.bomStatus === "reviewed"
          ? "shared"
          : p.bomStatus;
      const reportStatus =
        newIsFinalized && !p.isFinalized && p.reportStatus === "reviewed"
          ? "shared"
          : p.reportStatus;
      return {
        ...p,
        reportUrl: url,
        reportShared: true,
        isFinalized: newIsFinalized,
        bomStatus,
        reportStatus,
        ...(kgsCo2e !== undefined ? { kgsCo2e } : {}),
        ...(footprintBreakdownKgs !== undefined
          ? { footprintBreakdownKgs }
          : {})
      };
    });

    await updateSharedProducts(updatedProducts);
    await updateCollectionDoc("product-components", productId, {
      buyerIds: firebaseArrayUnionObj(buyerId)
    });
    setGeneratingProductId(null);
  };

  const onUploadError = () => setGeneratingProductId(null);

  const sharedProductIds = sharedProducts.map((p) => p.productId);
  const unsharedProducts = dbProducts.filter(({ id }) => {
    if (sharedProductIds.includes(id)) {
      return false;
    }
    const event = productLifecycleEvents.find((e) => e.productId === id);
    return !event?.bomStatus || event?.bomStatus === "confirmed";
  });

  const generatingProduct = generatingProductId
    ? dbProducts.find((p) => p.id === generatingProductId)
    : null;
  const mergedGeneratingProduct = generatingProduct
    ? getMergedProduct(generatingProduct)
    : null;

  return (
    <>
      {viewingProduct && (
        <SharedReportOverlay
          reportUrl={viewingProduct.reportUrl}
          title={`${viewingProduct.productName} Product Report`}
          isFinalized={isProductFinalized(viewingProduct.productId)}
          onClose={() => setViewingProduct(null)}
        />
      )}
      {mergedGeneratingProduct && (
        <ProductPdfCapture
          product={mergedGeneratingProduct}
          onUploaded={(url) =>
            onUploadComplete(
              generatingProductId,
              url,
              mergedGeneratingProduct.kgsCo2e,
              mergedGeneratingProduct.footprintBreakdownKgs
            )
          }
          onError={onUploadError}
        />
      )}
      <Grid container direction="column" spacing={2}>
        {buyerAssignedProducts.length > 0 && (
          <Grid item>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Products assigned by customer
            </Typography>
            <Grid container direction="column" spacing={1}>
              {buyerAssignedProducts.map(({ lifecycleId, name, bomStatus }) => (
                <Grid item key={lifecycleId}>
                  <GrayPaper style={{ padding: 12 }}>
                    <BuyerAssignedProductRow
                      name={name}
                      bomStatus={bomStatus}
                    />
                  </GrayPaper>
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}
        {!isReadOnly && unsharedProducts.length > 0 && (
          <Grid item>
            <Autocomplete
              key={selectorKey}
              options={unsharedProducts}
              getOptionLabel={(p) => p.name || ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Add a product to share"
                  size="small"
                />
              )}
              onChange={(_, product) => {
                if (product) {
                  onShare(product);
                  setSelectorKey((prev) => prev + 1);
                }
              }}
              value={null}
            />
          </Grid>
        )}
        {sharedProductIds.map((productId) => {
          const productDoc = dbProducts.find((p) => p.id === productId);
          const sharedEntry = getSharedEntry(productId);

          if (!productDoc || !sharedEntry) {
            return null;
          }

          return (
            <Grid item key={productId}>
              <GrayPaper style={{ padding: 16 }}>
                <SharedProductRow
                  product={productDoc}
                  sharedEntry={sharedEntry}
                  isFinalized={isProductFinalized(productId)}
                  isReadOnly={isReadOnly}
                  onView={() =>
                    setViewingProduct({
                      productId,
                      reportUrl: sharedEntry.reportUrl,
                      productName: productDoc.name
                    })
                  }
                  onUnshare={() => onUnshare(productId)}
                  onToggleBom={() =>
                    onToggleBom(productId, !sharedEntry.bomShared)
                  }
                  onToggleReport={() =>
                    onToggleReport(productId, !sharedEntry.reportShared)
                  }
                  generatingReport={generatingProductId === productId}
                />
              </GrayPaper>
            </Grid>
          );
        })}
        {sharedProductIds.length === 0 && isReadOnly && (
          <Grid item>
            <Typography variant="body2" color="textSecondary" align="center">
              No products have been shared yet.
            </Typography>
          </Grid>
        )}
        {unsharedProducts.length === 0 &&
          sharedProductIds.length === 0 &&
          !isReadOnly && (
            <Grid item>
              <Typography variant="body2" color="textSecondary" align="center">
                No products available to share.
              </Typography>
            </Grid>
          )}
      </Grid>
    </>
  );
};

export default ProductsSection;
